import React, { ReactNode } from 'react';
import ButtonDesigned from '../../../Buttons/ButtonDesigned';
import { DisplayFilePreview } from './DisplayFilePreview';
import { ConfirmDelete } from '../../ErrorComponents/ConfirmDelete';
import { deleteFile } from '../../../../../sp-firebase/storageFB';
import { DropOrBrowse } from './DropOrBrowse';
import { comment } from '@sp-types/FutureOrderType';
import { Add, ArrowLeft } from '@mui/icons-material';
import { FileTitle } from './FileTitle';
import { CommentsPanel } from './CommentsPanel';
import { Timestamp } from 'firebase/firestore';

enum PageStatus {
  INIT,
  ADDING,
  DELETE,
  COMMENT,
  GOTO_MANUAL,
}
interface Props {
  fileUrls: string[];
  filePaths: string[];
  dropzoneConfig: any;
  onDeleteFile: (_path: string) => Promise<void>;
  gotoManualInput: () => void;
  onCommentsChanged: (_comments: comment[]) => void;
  comments: comment[];
  options?: {
    isHideManual?: boolean;
    isShowEmpty?: boolean;
    disabled?: boolean;
  };
}
export const MultiFileUploadWizard: React.FC<Props> = ({
  fileUrls,
  filePaths,
  dropzoneConfig,
  onDeleteFile,
  gotoManualInput,
  comments,
  onCommentsChanged,
  options = { isHideManual: false, isShowEmpty: false, disabled: false },
}): ReactNode => {
  const [selectedFile, setSelectedFile] = React.useState(fileUrls?.length > 0 ? 0 : -1);
  const [state, setState] = React.useState(PageStatus.INIT);

  React.useEffect(() => {
    if (selectedFile < 0 || selectedFile > filePaths.length - 1) {
      selectFile(filePaths.length > 0 ? filePaths.length - 1 : -1);
    }
  }, [filePaths]);

  const onDeleteConfirm = async () => {
    const fileToDelete = filePaths[selectedFile];

    await onDeleteFile(fileToDelete);
    setSelectedFile(-1);

    setState(PageStatus.INIT);
  };

  const onGotoManual = async () => {
    await Promise.all(filePaths.map((path) => deleteFile(path)));

    setState(PageStatus.INIT);
    gotoManualInput();
  };

  const addFile = () => {
    setState(PageStatus.ADDING);
    setSelectedFile(-1);
  };

  const selectFile = (index: number) => {
    setSelectedFile(index);
    setState(index === -1 ? PageStatus.ADDING : PageStatus.INIT);
  };

  const filterComments = (allComments: comment[], fileIndex: number): comment[] =>
    allComments.filter(({ selectedFile }) => selectedFile === fileIndex);

  const onComment = (index: number) => {
    setSelectedFile(index);
    setState(PageStatus.COMMENT);
  };
  const onDelete = (index: number) => {
    setSelectedFile(index);
    setState(PageStatus.DELETE);
  };

  const onAddComment = (text: string, selectedFile: number) => {
    if (!options.disabled) {
      onCommentsChanged([...comments, { addedOn: Timestamp.now(), text, selectedFile }]);
    }
  };

  const deleteComments = (deleteComments: comment[]) => {
    if (!options.disabled) {
      const deleteSet = new Set(deleteComments.map(({ text }) => text));
      onCommentsChanged(comments.filter(({ text }) => !deleteSet.has(text)));
    }
  };

  return fileUrls?.length > 0 || options?.isShowEmpty ? (
    <div className={`flex gap-10`}>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between w-80 h-9">
          <p className="font-bold py-1">Preview</p>
          <ButtonDesigned
            style="outline"
            className="text-start"
            onButtonClick={addFile}
            disableButton={state === PageStatus.ADDING || options.disabled}
          >
            <Add /> Add file
          </ButtonDesigned>
        </div>
        <div className="flex justify-between gap-10 ">
          <div className="flex flex-col gap-3">
            {state === PageStatus.ADDING && !options.disabled ? (
              <DropOrBrowse dropzoneConfig={dropzoneConfig} onExit={() => setState(PageStatus.INIT)} />
            ) : (
              <div className="border border-gray-110 rounded w-80 h-80">
                <DisplayFilePreview fileUrl={fileUrls[selectedFile] || ''} />
              </div>
            )}
            {!options.isHideManual && (
              <>
                <div className="flex items-center">
                  <hr className="flex-grow border-gray-300" />
                  <span className="mx-4 text-gray-500">OR</span>
                  <hr className="flex-grow border-gray-300" />
                </div>
                <ButtonDesigned
                  style="outline"
                  type="button"
                  className="border-gray-125 "
                  onButtonClick={() => setState(PageStatus.GOTO_MANUAL)}
                >
                  <div className="flex gap-2 items-center text-lg text-gray-700">
                    <img src="/assets/icons/red/create-order.svg" />
                    Enter manual order
                  </div>
                </ButtonDesigned>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between w-80">
        <div className="flex justify-between h-11">
          <p className="font-bold py-1">Attach files - {fileUrls.length}</p>
        </div>
        <div className="flex flex-col flex-1 border border-gray-110 rounded w-80 p-2">
          <div className="flex flex-col flex-1 border-b border-gray-110">
            {filePaths.map((file, index) => (
              <div
                key={index}
                className={`relative cursor-pointer border-b border-gray-110 p-2 rounded-sm ${
                  selectedFile === index ? 'ring-[1.5px] ring-red' : ''
                }`}
              >
                <FileTitle
                  fileUrl={file}
                  onClick={() => selectFile(index)}
                  onDelete={() => onDelete(index)}
                  disabled={!!options.disabled}
                  hasComments={!!filterComments(comments || [], index)?.length}
                />
                {selectedFile === index && (
                  <div className="absolute -right-[64px] text-gray-110 text-lg top-0">
                    <ArrowLeft fontSize="large" />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div
            onClick={() => selectFile(-1)}
            className={`flex relative p-2 rounded-sm cursor-pointer ${selectedFile === -1 ? 'ring-1 ring-red' : ''}`}
          >
            <p className="flex-1">General comments</p>
            <div className="flex justify-end gap-1">
              <img src="/assets/icons/blue-flat/comment.svg" alt="com" className="h-6 w-6" />
            </div>
            {selectedFile === -1 && (
              <div className="absolute -right-[64px] text-gray-110 text-lg top-0">
                <ArrowLeft fontSize="large" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between w-80">
        <div className="flex justify-between h-11">
          <p className="font-bold py-1">Comments - {filterComments(comments || [], selectedFile).length || 0}</p>
        </div>
        <div className="flex flex-col flex-1 border border-gray-110 rounded w-80 p-2">
          <CommentsPanel
            disabled={options.disabled}
            comments={filterComments(comments, selectedFile)}
            onAddComment={(text) => onAddComment(text, selectedFile)}
            deleteComments={deleteComments}
          />
        </div>
      </div>
      {state === PageStatus.DELETE && (
        <ConfirmDelete
          onAction={onDeleteConfirm}
          onCancel={() => setState(PageStatus.INIT)}
          title="Confirmation"
          description="This action will remove the attached file from this order."
        />
      )}
      {state === PageStatus.GOTO_MANUAL && (
        <ConfirmDelete
          onAction={onGotoManual}
          onCancel={() => setState(PageStatus.INIT)}
          title="Confirmation"
          description="This action will remove the attached file and transfer you to manual input."
          actionText="Continue"
        />
      )}
    </div>
  ) : null;
};
